<template>
  <WizardBox title="New Contact">
      <template v-slot:tools>
        
      </template>
      <template v-slot:content>
        <FormGroup v-bind:form="contactForm">
          <FormWizard ref="contactWizard" :submitting="submitting" v-bind:tabList="tabList" @finished="handleSubmit">
            <template v-slot:content>
              
              <FormWizardStep index="1" v-bind:active="true">
                <FormRow label="Name"><FormInputText placeholder="Name" v-model="contact.name" field="name" /></FormRow>
                <FormRow label="Details">
                    <FormCheckbox label="Customer" v-model="contact.isCustomer" field="isCustomer" />&nbsp;&nbsp;&nbsp;
                    <FormCheckbox label="Supplier" v-model="contact.isSupplier" field="isSupplier" />
                </FormRow>
                <FormRow v-if="hasMyobIntegration && contact.isCustomer && contact.isSupplier">
                  <FormPanel title="MYOB Warning" panelClass="panel-warning">
                    This contact is marked as both a customer and supplier, which is not supported with the MYOB integration. Please select either customer or supplier.<br />
                    If both are required, you will need to create two separate contacts, one for the customer details and one for the supplier details.
                  </FormPanel>                  
                </FormRow>
                <FormRow label="Phone"><FormInputText placeholder="Phone" v-model="contact.phone" field="phone" /></FormRow>
                <FormRow label="Fax"><FormInputText placeholder="Fax" v-model="contact.fax" field="fax" /></FormRow>
                <FormRow label="Website"><FormInputText placeholder="Website" v-model="contact.website" field="website" /></FormRow>
                <FormRow label="Notes"><FormInputTextArea placeholder="Notes" v-model="contact.note" field="note" /></FormRow>

                <FormHorizontalRule />

                <FormRow label="Account Manager" helpTip="The user who manages this contact's account">
                  <UserSearchableSelect v-model="contact.managerUserId" field="managerUserId" :allowNone="true" />
                </FormRow>
                <FormRow label="Default Location" helpTip="The default location for this contact, used as the default location for orders and purchase orders made with this contact">
                  <AccountLocationSelect v-model="contact.accountLocationId" field="accountLocationId" />
                </FormRow>

                <FormRow label="Labels"><FormLabelTag popupName="Contact Labels" v-model="contact.labelList" labelType="contact" field="labelList" /></FormRow>

                <!-- Integrations -->
                <div v-if="hasXeroIntegration">
                  <FormHorizontalRule />
                  <FormRow label="Integrations">

                    <FormButton v-if="hasXeroIntegration && !isLinkedToXeroContact" class="btn-primary" @click="onImportXeroContact"><i class="far fa-download"></i> Import From Xero Contact</FormButton>

                    <FormButton v-if="hasXeroIntegration && isLinkedToXeroContact" class="btn-default" @click="removeXeroIntegrationLink"><i class="far fa-times"></i> Remove Xero Link</FormButton>

                  </FormRow>
                </div>
              </FormWizardStep>

              <FormWizardStep index="2">
                <FormRow label="Title"><FormSelect v-model="contact.personTitle" :options="personTitleOptions"/></FormRow>
                <FormRow label="First name"><FormInputText placeholder="Firstname" v-model="contact.personFirstName" field="personFirstName" /></FormRow>
                <FormRow label="Last name"><FormInputText placeholder="Lastname" v-model="contact.personLastName" field="personLastName" /></FormRow>
                <FormRow label="Position"><FormInputText placeholder="Position" v-model="contact.personPosition" field="personPosition" /></FormRow>
                <FormRow label="Email"><FormInputEmail placeholder="Email" v-model="contact.personEmail" field="personEmail" /></FormRow>
                <FormRow label="Phone"><FormInputText placeholder="Phone" v-model="contact.personPhone" field="personPhone" /></FormRow>
                <FormRow label="Mobile"><FormInputText placeholder="Mobile" v-model="contact.personMobile" field="personMobile" /></FormRow>
                <FormRow label="Notes"><FormInputTextArea placeholder="Notes" v-model="contact.personNote" field="personNote" /></FormRow>
                <FormRow label="Labels"><FormLabelTag popupName="Person Labels" v-model="contact.personLabelList" labelType="contactPerson" field="personLabelList" /></FormRow>
              </FormWizardStep>

              <FormWizardStep index="3">
                <FormRow label="Street Address">
                  <FormInputAddressSearch
                    placeholder="Address Line 1"
                    v-model="contact.addressLine1"
                    field="addressLine1"
                    @updateAddress2="contact.addressLine2 = $event"
                    @updateSuburb="contact.addressSuburb = $event"
                    @updateState="contact.addressState = $event"
                    @updatePostcode="contact.addressPostcode = $event"
                    @updateCountry="contact.addressCountry = $event"
                   />
                </FormRow>
                <FormRow label=" "><FormInputText placeholder="Address Line 2" v-model="contact.addressLine2" field="addressLine2" /></FormRow>
                <FormRow label="Suburb"><FormInputText placeholder="Suburb" v-model="contact.addressSuburb" field="addressSuburb" /></FormRow>
                <FormRow label="Postcode"><FormInputText placeholder="Postcode" v-model="contact.addressPostcode" field="addressPostcode" /></FormRow>
                <FormRow label="State"><FormInputText placeholder="State" v-model="contact.addressState" field="addressState" /></FormRow>
                <FormRow label="Country"><FormInputText placeholder="Country" v-model="contact.addressCountry" field="addressCountry" /></FormRow>
                <FormRow label="Labels"><FormLabelTag popupName="Address Labels" v-model="contact.addressLabelList" labelType="contactAddress" field="addressLabelList" /></FormRow>
                <FormRow label="Delivery Tag" helpTip="Limited to a single label, displayed in the production labels / order sheets">
                  <FormLabelTag popupName="Delivery Tag" :singleLabelMode="true" v-model="contact.deliveryLabelList" labelType="contactAddressDelivery" field="deliveryLabelList" />
                </FormRow>
              </FormWizardStep>

              <FormWizardStep index="4">
                <div v-if="isCustomer && (isCustomerPricingSimple || !createPriceList)">
                  <FormRow label="Customer Default Sales Rate">
                    <SalesRateSelect v-model="contact.defaultSalesRateId" field="defaultSalesRateId" />
                  </FormRow>
                  <div v-if="!isCustomerPricingSimple" class="mt-2">
                    <FormButton class="btn-primary" @click="setupCustomerPriceList">Setup Customer Price List</FormButton>
                  </div>
                </div>
                <div v-if="isCustomer && !isCustomerPricingSimple && createPriceList">
                  <div class="row">
                    <div class="col-12">
                      <ActionDropMenu class="float-right">
                        <DropdownMenuItem @click="importPriceListTemplate" icon="fa-file-import">Import Price List Template</DropdownMenuItem>
                      </ActionDropMenu>

                      <ContentTitle>Customer Pricelist</ContentTitle>
                      <ContentSubTitle>Setup the initial pricelist for the new customer</ContentSubTitle>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12">
                      <CustomerPriceListEditor 
                        ref="customerPriceList"
                        :editing="true"
                        :priceListIntegrationType="priceListIntegrationType"
                        @priceGroupOverrideSettingsUpdated="priceListPriceGroupOverrideSettings = $event"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="!isCustomer">
                  No pricing available in contact creation
                </div>
              </FormWizardStep>

              <FormWizardStep index="5">
                <FormRow label="Default Despatch Task Type" helpTip="The default task type selected for new orders for this contact to be used for order despatching">
                  <TaskTypeSelect v-model="contact.defaultDespatchTaskTypeId" :despatchEnabledOnly="true" :allowNone="true" />
                </FormRow>

                <FormRow label="Default Tax Rate">
                  <TaxRateSelect v-model="contact.defaultTaxRateId" :allowNone="true" />
                </FormRow>
              </FormWizardStep>
            </template>
          </FormWizard>

        </FormGroup>
      </template>
    </WizardBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import detectUnload from '@/mixins/detectUnload'

import WizardBox from '@/components/wizardBox'
import SalesRateSelect from '@/modules/pricing/components/salesRateSelect'
import UserSearchableSelect from '@/modules/users/components/userSearchableSelect'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import TaxRateSelect from '@/modules/admin/components/taxRateSelect'
import CustomerPriceListEditor from '@/modules/pricing/components/customerPriceListEditor'
import CustomerPriceListSetupDialog from '@/modules/pricing/views/customerPriceListSetupDialog'
import CustomerPriceListImportTemplateDialog from '@/modules/pricing/views/customerPriceListImportTemplateDialog'
import TaskTypeSelect from '@/modules/tasks/components/taskTypeSelect'

import XeroImportContactDialog from "@/integrations/xero/views/xeroImportContactDialog.vue";

import { PersonTitleLookup, PricingSystemTypes, IntegrationTypeCodes } from '@/store/types'


export default {
  name: "contactnew",
  mixins: [form, detectUnload],
  components: {
    WizardBox,
    UserSearchableSelect,
    SalesRateSelect,
    AccountLocationSelect,
    TaxRateSelect,
    CustomerPriceListEditor,
    TaskTypeSelect
  },
  props: {
    contactDefaults: Object
  },
  data() {
    var newContactData = {
      breadcrumbs: [
        { title: "Contacts", link: "/contacts" },
        { title: "New Contact" }
      ],
      personTitleOptions: PersonTitleLookup,
      tabList: [
        { title: 'Details' },
        { title: 'Default Contact' },
        { title: 'Default Address' },
        { title: 'Pricing' },
        { title: 'Info' },
      ],
      contactForm: {},
      contact: {
        isCustomer: true,
        isSupplier: false,
        contactType: 'company',
        defaultDespatchTaskTypeId: null,
        defaultSalesRateId: null,
        defaultTaxRateId: null,
        managerUserId: null,
        accountLocationId: null,
        personTitle: PersonTitleLookup[0].name,
        labelList: [],
        personLabelList: [],
        addressLabelList: [],
        deliveryLabelList: [],
        integrationData: {},
        priceListPriceGroupOverrideSettings: [],
        addressLine1: '',
        addressLine2: '',
        addressSuburb: '',
        addressState: '',
        addressPostcode: '',
        addressCountry: '',
      },
      createPriceList: false,
      priceListIntegrationType: null,
      isContactEdited: true,
      submitting: false
    }

    if(this.$props.contactDefaults) {
      newContactData.contact = _.merge(newContactData.contact, this.$props.contactDefaults)
    }

    return newContactData
  },
  beforeRouteLeave (to, from, next) {
    if(!this.$data.isContactEdited)
      return next(true)
    
    this.confirmMessage(`Cancel New Contact?`, `You have unsaved changes with the new Contact, are you sure you want to leave?`)
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  mounted() {
    this.contact.defaultTaxRateId = this.accountTaxRateId
  },
  computed: {
    ...mapState({
      accountPricingSystemType: state => state.account.pricingSystemType,
      activeIntegrations: state => state.profile.activeIntegrations,
      accountTaxRateId: state => state.account.salesTaxRate.id
    }),
    isLinkedToXeroContact() {
      if(!this.contact.integrationData || !this.contact.integrationData.xero)
        return false

      return this.contact.integrationData.xero.active
    },
    isCustomer() {
      return this.contact.isCustomer
    },
    isSupplier() {
      return this.contact.isSupplier
    },
    isCustomerPricingSimple() {
      return this.accountPricingSystemType == PricingSystemTypes.simple
    }
  },
  watch: {
    accountTaxRateId: function() {
      if(!this.contact.defaultTaxRateId)
        this.contact.defaultTaxRateId = this.accountTaxRateId
    }
  },
  methods: {
    onBeforeUnload: function(event) {
      if(!this.$data.isContactEdited)
        return null

      var confirmationMessage = `You have unsaved changes with this new Contact. Are you sure you want to exit?`

      event.returnValue = confirmationMessage
      return confirmationMessage
    },
    handleSubmit() {
      var contactData = _.cloneDeep(this.$data.contact)
      contactData.labelList = contactData.labelList.map(label => label.id)
      contactData.personLabelList = contactData.personLabelList.map(label => label.id)
      contactData.addressLabelList = contactData.addressLabelList.map(label => label.id)
      contactData.deliveryLabelList = contactData.deliveryLabelList.map(label => label.id)


      // Customer Pricing data for the contact to start with if not using a simple pricing system
      if(this.isCustomer && !this.isCustomerPricingSimple) {
        if(this.createPriceList) {
          contactData.priceData = this.$refs.customerPriceList.buildServerPriceListUpdateListData()
          contactData.priceGroupOverrideSettings = this.priceListPriceGroupOverrideSettings || []
          contactData.priceListIntegrationType = this.priceListIntegrationType
        }
      }

      this.submitting = true
      this.$data.contactForm = this.clearFormResponse()

      this.$api.post('/contacts', contactData)
      .then(response => {
          this.submitting = false

          // Clear editing flag to prevent route guard confirmation
          this.$data.isContactEdited = false

          this.successToast('Contact Created')
          this.$router.push(`/contacts/${response.data.id}`)
      })
      .catch(err => {
          // Process errors
          this.$data.contactForm = this.processFormResponse(err)
          this.$refs.contactWizard.displayErrors()
      })
      .finally(() => {
        this.submitting = false
      })
    },
    onImportXeroContact() {
      this.displayModal(XeroImportContactDialog)
      .then(contactData => {
        if(contactData) {

          // Copy from Xero contact info
          this.contact = Object.assign({}, this.contact, contactData)
          this.$forceUpdate()
        }        
      });
    },
    removeXeroIntegrationLink() {
      if(this.$data.contact.integrationData[IntegrationTypeCodes.xero] != undefined) {
        this.$data.contact.integrationData[IntegrationTypeCodes.xero] = {}
      }
    },
    setupCustomerPriceList() {
      // Display the dialog to setup a new price list
      this.displayModal(CustomerPriceListSetupDialog)
      .then((result) => {
        if(result) {
          // Start new price list editing
          this.priceListIntegrationType = result
          this.createPriceList = true
        }
      })
    },

    importPriceListTemplate() {
      this.displayModal(CustomerPriceListImportTemplateDialog)
      .then((priceListTemplateId) => {
        if(priceListTemplateId) {

          // Copy the template into the current price list
          this.$refs.customerPriceList.importFromTemplate(priceListTemplateId)
          .then(() => {
            this.successToast('Template Imported')
          })
          .catch((err) => {
            this.displayNetworkError(err)
          })          
        }
      })
    }
  }
};
</script>
